import { useEffect, useState } from 'react';
import { Search, Plus } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import DocumentUpload from '../components/admin/DocumentUpload';
import DocumentTable from '../components/admin/DocumentTable';
import DeleteDocumentModal from '../components/admin/DeleteDocumentModal';
import { DOCUMENT_CATEGORIES } from '../constants/documentCategories';
import { getChurches } from '../lib/churches';
import { listDocuments, getSignedUrl, deleteDocument } from '../lib/documents';

const selectClass =
  'rounded-md border border-slate-200 px-3 py-2 text-sm text-[#00384D] bg-white focus:outline-none focus:ring-2 focus:ring-[#007A96]/30 focus:border-[#007A96] transition-colors';

export default function AdminDocuments() {
  const churches = getChurches();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [churchId, setChurchId] = useState('');
  const [year, setYear] = useState('');

  const [showUpload, setShowUpload] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function loadDocuments() {
    setLoading(true);
    setError('');
    try {
      const data = await listDocuments({
        search: search || undefined,
        category: category || undefined,
        churchId: churchId || undefined,
        year: year || undefined,
      });
      setDocuments(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, churchId, year]);

  async function handleView(doc) {
    try {
      const url = await getSignedUrl(doc.file_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleConfirmDelete() {
    setDeleting(true);
    try {
      await deleteDocument(docToDelete);
      setDocToDelete(null);
      loadDocuments();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  function handleUploaded() {
    setShowUpload(false);
    setSuccessMessage('Document uploaded successfully.');
    loadDocuments();
    setTimeout(() => setSuccessMessage(''), 4000);
  }

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-[22px] font-semibold text-[#00384D] tracking-tight">Documents</h1>
          <p className="text-slate-500 text-sm mt-1">Manage district documents and reports.</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 bg-[#00384D] text-white text-sm font-medium rounded-md px-4 py-2.5 hover:bg-[#00495F] transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          Upload Document
        </button>
      </div>

      {successMessage && (
        <div className="mb-4 rounded-md border border-[#007A96]/20 bg-[#007A96]/5 px-4 py-3 text-sm text-[#007A96]">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md border border-[#C21E26]/20 bg-[#C21E26]/5 px-4 py-3 text-sm text-[#C21E26]">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents..."
            className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm text-[#00384D] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007A96]/30 focus:border-[#007A96] transition-colors"
          />
        </div>

        <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectClass}>
          <option value="">All Categories</option>
          {DOCUMENT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select value={churchId} onChange={(e) => setChurchId(e.target.value)} className={selectClass}>
          <option value="">All Churches</option>
          {churches.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <input
          type="number"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          placeholder="Year"
          className="w-24 rounded-md border border-slate-200 px-3 py-2 text-sm text-[#00384D] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007A96]/30 focus:border-[#007A96] transition-colors"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 mt-4 shadow-sm overflow-hidden">
        {loading ? (
          <p className="px-6 py-14 text-sm text-slate-400 text-center">Loading documents…</p>
        ) : (
          <DocumentTable documents={documents} onView={handleView} onDelete={setDocToDelete} />
        )}
      </div>

      {showUpload && (
        <DocumentUpload onClose={() => setShowUpload(false)} onUploaded={handleUploaded} />
      )}

      <DeleteDocumentModal
        document={docToDelete}
        onCancel={() => setDocToDelete(null)}
        onConfirm={handleConfirmDelete}
        deleting={deleting}
      />
    </AdminLayout>
  );
}
